import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DatePicker from "react-datepicker";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import InputLabel from "@mui/material/InputLabel";
import ReceiptIcon from "@mui/icons-material/Receipt";
import TagIcon from "@mui/icons-material/Tag";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

import "react-datepicker/dist/react-datepicker.css";

import { StoreState } from "../../redux/configure-store";
import { createInvoice } from "../admin-actions";
import Loading from "../../common/components/Loading";
import {
  calculateTotalHoursForTimesheets,
  formatTimesheetDate,
  generateInvoiceId,
  transformMinutesTimeFormatToDecimals,
} from "../../common/utils";
import dayjs from "../../common/date";
import { Invoice, Timesheet } from "../types";
import { backgrounds } from "../../theme/theme";

const createInitialErrorState = () => ({
  publicId: "",
  totalNumberOfHours: "",
  periodStartDate: "",
  periodEndDate: "",
  paymentDueAt: "",
  secondaryItemAmount: "",
  secondaryItemDescription: "",
  salesTaxAmount: "",
  totalPaymentAmount: "",
  currency: "",
  paymentMethod: "",
});

function AdminInvoiceCreate(props: any) {
  const { allInvoices, engagement, isLoading, allTimesheets, selectedTimesheetIds } = props;

  const [formData, setFormData] = useState({
    publicId: "",
    engagementId: null,
    userId: null,
    totalNumberOfHours: "0.00",
    secondaryItemAmount: 0.0,
    secondaryItemDescription: null,
    periodStartDate: null,
    periodEndDate: null,
    paymentDueAt: null,
    currency: null,
    paymentMethod: "",
    salesTaxAmount: "0.00",
    totalPaymentAmount: 0.0,
  });

  const [errors, setErrors] = useState(createInitialErrorState());

  const onChange = useCallback((e: any) => {
    const name = e.target.name;
    const value = e.target.value;
    setFormData((prevState: any) => ({ ...prevState, [name]: value }));
    const hourlyRate = engagement.hourlyRate;
    setFormData((prevState: any) => {
      const salesTaxAmount = parseFloat(prevState.salesTaxAmount || "0");
      const secondaryItemAmount = parseFloat(
        prevState.secondaryItemAmount || "0"
      );
      return {
        ...prevState,
        totalPaymentAmount:
          salesTaxAmount +
          secondaryItemAmount +
          hourlyRate * parseFloat(prevState.totalNumberOfHours),
      };
    });
  }, [engagement]);

  useEffect(() => {
    if (isLoading || !engagement?.user?.id) {
      return;
    }
    // Filter by user ID (not engagement ID) since invoice IDs are per-user
    const invoicesForUser = allInvoices
      .filter((i: Invoice) => i.user.id === engagement.user.id)
      .sort((a: Invoice, b: Invoice) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    if (invoicesForUser.length) {
      const lastInvoice = invoicesForUser[invoicesForUser.length - 1];
      onChange({
        target: {
          name: "publicId",
          value: generateInvoiceId(lastInvoice.publicId),
        }
      });
    }
    onChange({
      target: {
        name: "paymentMethod",
        value: engagement.user.paymentMethod,
      }
    });
  }, [isLoading, engagement, onChange, allInvoices]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    const _selectedTimesheets = allTimesheets
      .filter((t: Timesheet) => selectedTimesheetIds.includes(t.id))
      .sort((a: Timesheet, b: Timesheet) => a.id - b.id)
    if (!_selectedTimesheets.length) {
      return;
    }

    const totalHoursInMinutesOrDecimals = calculateTotalHoursForTimesheets(_selectedTimesheets);
    onChange({
      target:
      {
        name: "totalNumberOfHours",
        value: _selectedTimesheets[0].timeFormat === 'decimals'
          ? totalHoursInMinutesOrDecimals
          : transformMinutesTimeFormatToDecimals(totalHoursInMinutesOrDecimals)
      }
    });
    handlePeriodStartDateChange(new Date(formatTimesheetDate(_selectedTimesheets[0].startDate)))
    const endDate = new Date(formatTimesheetDate(_selectedTimesheets[_selectedTimesheets.length - 1].endDate));
    handlePeriodEndDateChange(endDate)
    handlePaymentDueAtChange(dayjs(endDate).add(20, 'day').toDate());
  }, [isLoading, onChange, selectedTimesheetIds, allTimesheets]);

  function handlePeriodStartDateChange(date: Date) {
    setFormData((prevState: any) => (
      { ...prevState, periodStartDate: date }
    ));
  };

  function handlePeriodEndDateChange(date: Date) {
    setFormData((prev: any) => ({
      ...prev,
      periodEndDate: date
    }));
  };

  function handlePaymentDueAtChange(date: Date) {
    setFormData((prev: any) => ({ ...prev, paymentDueAt: date }));
  };

  function handlePaymentMethodChange(e: any) {
    setFormData((prev: any) => ({ ...prev, paymentMethod: e.target.value }));
  }

  function onSubmit(e: any) {
    e.preventDefault();
    setErrors(createInitialErrorState());

    const emptyValueErrorMessage = "Value can't be empty";
    const _errors = createInitialErrorState();

    if (formData.publicId.length === 0) {
      _errors.publicId = emptyValueErrorMessage;
    }

    if (formData.totalNumberOfHours.length === 0) {
      _errors.totalNumberOfHours = emptyValueErrorMessage;
    }

    if (!formData.periodStartDate) {
      _errors.periodStartDate = emptyValueErrorMessage;
    }

    if (!formData.periodEndDate) {
      _errors.periodEndDate = emptyValueErrorMessage;
    }

    if (!formData.paymentDueAt) {
      _errors.paymentDueAt = emptyValueErrorMessage;
    }

    if (!formData.paymentMethod) {
      _errors.paymentMethod = emptyValueErrorMessage;
    }

    if (formData.salesTaxAmount === "") {
      _errors.salesTaxAmount = emptyValueErrorMessage;
    }

    if (formData.secondaryItemAmount !== 0 && isNaN(formData.secondaryItemAmount)) {
      _errors.secondaryItemAmount = "Secondary Item amount must be a number.";
    }

    if (isNaN(formData.totalPaymentAmount)) {
      _errors.totalPaymentAmount = "This amount is not valid";
    }

    setErrors(_errors);

    const hasErrors = Object.values(_errors).some(
      (error: string) => error.length !== 0
    );

    if (!hasErrors) {
      props.createInvoice({
        publicId: formData.publicId,
        engagementId: engagement.id,
        userId: engagement.user.id,
        currency: engagement.currency,
        paymentMethod: formData.paymentMethod,
        salesTaxAmount: parseFloat(formData.salesTaxAmount),
        secondaryItemAmount: formData.secondaryItemAmount,
        secondaryItemDescription: formData.secondaryItemDescription,
        totalPaymentAmount: formData.totalPaymentAmount,
        totalNumberOfHours: parseFloat(formData.totalNumberOfHours),
        periodStartDate: formData.periodStartDate,
        periodEndDate: formData.periodEndDate,
        paymentDueAt: formData.paymentDueAt,
      });
    }
  };

  if (isLoading) {
    return <Loading></Loading>;
  }

  if (!engagement || !engagement.id) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">You must select an engagement first</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: backgrounds.page,
        py: 4,
        px: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 600,
          mx: "auto",
          p: { xs: 3, sm: 4 },
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: "primary.main",
              mb: 2,
            }}
          >
            <ReceiptIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Typography component="h1" variant="h5" fontWeight={600}>
            Create Invoice
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Generate a new invoice for this engagement
          </Typography>
        </Box>

        {/* Engagement Info */}
        <Box
          sx={{
            backgroundColor: backgrounds.surface,
            borderRadius: 1,
            p: 2,
            mb: 3,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Client
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {engagement.clientName}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Developer
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {engagement.user.firstName} {engagement.user.lastName}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Box component="form" onSubmit={onSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                id="publicId"
                name="publicId"
                label="Invoice ID"
                autoFocus
                autoComplete="off"
                value={formData.publicId}
                onChange={onChange}
                error={!!errors.publicId}
                helperText={errors.publicId}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TagIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="totalNumberOfHours"
                name="totalNumberOfHours"
                label="Number of Hours"
                autoComplete="off"
                value={formData.totalNumberOfHours}
                onChange={onChange}
                error={!!errors.totalNumberOfHours}
                helperText={errors.totalNumberOfHours}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="currency"
                name="currency"
                label="Currency"
                autoComplete="off"
                disabled
                value={engagement.currency}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.paymentMethod}>
                <InputLabel id="payment-method-label">Payment Method *</InputLabel>
                <Select
                  labelId="payment-method-label"
                  id="payment-method"
                  value={formData.paymentMethod}
                  label="Payment Method *"
                  name="paymentMethod"
                  onChange={handlePaymentMethodChange}
                >
                  <MenuItem value="wise">Wise</MenuItem>
                  <MenuItem value="bmoUsd">BMO USD</MenuItem>
                  <MenuItem value="bitcoin">Bitcoin</MenuItem>
                  <MenuItem value="stablecoin">Stablecoin</MenuItem>
                </Select>
                {errors.paymentMethod && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.paymentMethod}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Invoice Period
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth error={!!errors.periodStartDate}>
                <FormLabel sx={{ mb: 1 }}>Start Date *</FormLabel>
                <DatePicker
                  selected={formData.periodStartDate}
                  onChange={handlePeriodStartDateChange}
                  customInput={<TextField fullWidth size="small" />}
                />
                {errors.periodStartDate && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.periodStartDate}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth error={!!errors.periodEndDate}>
                <FormLabel sx={{ mb: 1 }}>End Date *</FormLabel>
                <DatePicker
                  selected={formData.periodEndDate}
                  onChange={handlePeriodEndDateChange}
                  customInput={<TextField fullWidth size="small" />}
                />
                {errors.periodEndDate && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.periodEndDate}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth error={!!errors.paymentDueAt}>
                <FormLabel sx={{ mb: 1 }}>Due Date *</FormLabel>
                <DatePicker
                  selected={formData.paymentDueAt}
                  onChange={handlePaymentDueAtChange}
                  customInput={<TextField fullWidth size="small" />}
                />
                {errors.paymentDueAt && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.paymentDueAt}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Additional Items (Optional)
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="secondaryItemAmount"
                name="secondaryItemAmount"
                label="Secondary Item Amount"
                autoComplete="off"
                value={formData.secondaryItemAmount}
                onChange={onChange}
                error={!!errors.secondaryItemAmount}
                helperText={errors.secondaryItemAmount}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="secondaryItemDescription"
                name="secondaryItemDescription"
                label="Secondary Item Description"
                autoComplete="off"
                value={formData.secondaryItemDescription}
                onChange={onChange}
                error={!!errors.secondaryItemDescription}
                helperText={errors.secondaryItemDescription}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Totals
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                id="salesTaxAmount"
                name="salesTaxAmount"
                label="Sales Tax Amount"
                autoComplete="off"
                value={formData.salesTaxAmount}
                onChange={onChange}
                error={!!errors.salesTaxAmount}
                helperText={errors.salesTaxAmount}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  backgroundColor: "#e8f5e9",
                  borderRadius: 1,
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Total Payment Amount
                </Typography>
                <Typography variant="h6" fontWeight={600} color="success.dark">
                  {formData.totalPaymentAmount}
                </Typography>
                {errors.totalPaymentAmount && (
                  <Typography variant="caption" color="error">
                    {errors.totalPaymentAmount}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            sx={{
              mt: 3,
              py: 1.5,
              fontWeight: 600,
            }}
          >
            Create Invoice
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

const mapStateToProps = (storeState: StoreState) => ({
  allTimesheets: storeState.admin.timesheets,
  allInvoices: storeState.admin.invoices,
  selectedTimesheetIds: storeState.admin.selectedTimesheetIds,
});

export default connect(mapStateToProps, { createInvoice })(AdminInvoiceCreate);
